/**
 * The settings page's own rules, ported from `userSettingsController`
 * (dealer/assets/js/controllers/manage/users.js) and the business-schedule
 * editor (dealer/assets/js/components/dealr-business-schedule-editor.js).
 * The legacy copies stay until `account-security` is fully rolled
 * out, because flag-off must leave the legacy screen untouched; delete them
 * with the flag.
 */

import type {
  BusinessSchedule,
  GoogleConnection,
  GoogleService,
  MicrosoftConnection,
  ScheduleAccount,
} from './domain';

// ── Google ─────────────────────────────────────────────────────────────────

export function visibleGoogleServices(connection: GoogleConnection | null): GoogleService[] {
  return ((connection && connection.services) || []).filter((service) => service.available || service.connected);
}

/** Microsoft 365 shows only when the server offers it and lists a service, as the legacy page did. */
export function visibleMicrosoftServices(connection: MicrosoftConnection | null): GoogleService[] {
  return connection && connection.available ? visibleGoogleServices(connection) : [];
}

// ── What's New categories ──────────────────────────────────────────────────

export interface WhatsNewCategory {
  slug: string;
  label: string;
}

/** Opt-out model: absent from `unsubscribed` means subscribed. */
export function whatsNewModel(
  categories: Record<string, string> | undefined,
  unsubscribed: readonly string[] | undefined,
): { categories: WhatsNewCategory[]; subscribed: Record<string, boolean> } {
  const catalog = categories || {};
  const off = unsubscribed || [];
  const list = Object.keys(catalog).map((slug) => ({ slug, label: catalog[slug] ?? slug }));
  const subscribed: Record<string, boolean> = {};
  for (const cat of list) subscribed[cat.slug] = off.indexOf(cat.slug) === -1;
  return { categories: list, subscribed };
}

export function whatsNewUnsubscribed(categories: readonly WhatsNewCategory[], subscribed: Record<string, boolean>): string[] {
  return categories.filter((cat) => !subscribed[cat.slug]).map((cat) => cat.slug);
}

/** The Titles category only shows to a dealership with the titles service. */
export function whatsNewCategoryVisible(cat: WhatsNewCategory, hasTitlesService: boolean): boolean {
  return cat.slug !== 'titles' || hasTitlesService;
}

// ── Notification preferences ───────────────────────────────────────────────

export type NotificationPrefGroupKey = 'summary' | 'leads' | 'lead_conversations' | 'credit_deals' | 'repair_orders' | 'loans' | 'titles' | 'admin';

export interface NotificationPrefGroup {
  key: NotificationPrefGroupKey;
  label: string;
  lead: string;
}

/** The order the settings page shows notification groups in. */
export const NOTIFICATION_PREF_GROUPS: ReadonlyArray<NotificationPrefGroup> = [
  { key: 'summary', label: 'Daily summary', lead: 'A morning count of what needs your attention.' },
  { key: 'leads', label: 'Leads', lead: 'New leads, assignments, appointments and activity on leads you own.' },
  { key: 'lead_conversations', label: 'Lead conversations', lead: 'Texts, emails, calls and chats from customers on your leads.' },
  { key: 'credit_deals', label: 'Credit & deals', lead: 'Credit applications, lender decisions, digital deals and deal conversations.' },
  { key: 'repair_orders', label: 'Repair orders', lead: 'Customer replies on repair order (RO) conversations.' },
  { key: 'loans', label: 'Loans', lead: 'Customer replies on loan conversations.' },
  { key: 'titles', label: 'Titles', lead: 'Updates on the title transactions you submit.' },
  { key: 'admin', label: 'Admin', lead: 'Title processing alerts for Dealr staff.' },
];

export interface NotificationPrefType {
  name: string;
  help: string;
  key: string;
  group: NotificationPrefGroupKey;
  push?: boolean;
  email?: boolean;
}

export interface NotificationPrefContext {
  isAdmin: boolean;
  settingsAdmin: boolean;
  dtsDealer: boolean;
  routeOneFull: boolean;
  titlingAccess: boolean;
  commsV2: boolean;
  serviceCommunication: boolean;
  dealCommunication: boolean;
  loanCommunication: boolean;
}

const DEALER_PREFS: NotificationPrefType[] = [
  { name: 'Morning Needs Attention', help: 'Sent each morning with a count of tasks/new messages/new credit applications.', key: 'morning_needs_attention', group: 'summary', push: true },
  { name: 'New Credit Application', help: 'Sent when a new credit application is submitted if you have permission to view credit applications.', key: 'new_credit_application', group: 'credit_deals', push: true, email: true },
  { name: 'New Lead Credit Application', help: "Sent when a new credit application received on a lead you're assigned.", key: 'new_lead_credit_application', group: 'credit_deals', push: true },
  { name: 'New Lead', help: 'Sent when a new lead is received.', key: 'new_lead', group: 'leads', push: true, email: true },
  { name: 'Lead Skate Alert', help: 'Sent when a another user was detected to have possibly skated (stolen or copied) a lead you own.', key: 'lead_skate_alert', group: 'leads', push: true, email: true },
  { name: 'New Lead Text', help: "Sent when a new text is received on a lead you're assigned. If email is enabled, the text message will be forwarded to your inbox and you can reply via email to send a text back.", key: 'lead_new_text', group: 'lead_conversations', push: true, email: true },
  { name: 'New Lead Email', help: "Sent when a new email is received on a lead you're assigned. (This is the email that is forwarded from the customer so you can reply to the email in your inbox and have it be routed through dealr.cloud and back to the customer.)", key: 'lead_new_email', group: 'lead_conversations', push: true, email: true },
  { name: 'Incoming Call', help: "Sent when an incoming call is received on a lead you're assigned. If email is enabled, you'll receive an email notification about the call.", key: 'lead_incoming_call', group: 'lead_conversations', push: true, email: true },
  { name: 'New Facebook Message Received', help: 'Sent when a new message is received from Facebook.', key: 'lead_new_fb_message', group: 'lead_conversations', push: true },
  { name: 'New Website Chat Message Received', help: "Sent when a new website chat message is received on a lead you're assigned.", key: 'lead_new_chat_message', group: 'lead_conversations', push: true },
  { name: 'New Lead Comment', help: "Sent when another user adds a comment to a lead you're assigned.", key: 'lead_new_comment', group: 'leads', push: true },
  { name: 'Lead Assigned', help: "Sent when you're assigned a new lead.", key: 'lead_assigned', group: 'leads', push: true, email: true },
  { name: 'Lead Taken', help: 'Sent when another a lead is taken from you and assigned to another user. This requires that a dealership setting be turned on in Manage > Settings', key: 'lead_taken', group: 'leads', push: true, email: true },
  { name: 'Lead Auto Merge', help: 'Sent when an incoming lead is auto-merged with one of the leads assigned to you.', key: 'lead_auto_merge', group: 'leads', push: true },
  { name: 'Lead Upcoming Appointment', help: 'Sent when 1 hour and 15 minutes before an appointment. If appointment is the next morning it will send a notification the night before.', key: 'lead_upcoming_appointment', group: 'leads', push: true },
  { name: 'Digital Deal Received', help: 'Sent when a digital deal is received into the system.', key: 'digital_deal_received', group: 'credit_deals', push: true, email: true },
];

const ROUTE_ONE_PREF: NotificationPrefType = {
  name: 'RouteOne Credit App Decision',
  help: "Sent when a lender returns a credit decision on a deal you're assigned.",
  key: 'route_one_credit_decision',
  group: 'credit_deals',
  push: true,
  email: true,
};

const TITLE_PREFS: NotificationPrefType[] = [
  { name: 'Title Transaction Rejected', help: 'Sent each time a title transaction is rejected or changes are requested.', key: 'title_transaction_rejected', group: 'titles', email: true },
  { name: 'Title Transaction Posted', help: 'Sent each time a title transaction is posted and approved', key: 'title_transaction_approved', group: 'titles', email: true },
];

const MODULE_GROUP = { service: 'repair_orders', deal: 'credit_deals', loan: 'loans' } as const;

function modulePrefs(prefix: 'service' | 'deal' | 'loan', noun: string, owner: string): NotificationPrefType[] {
  const group = MODULE_GROUP[prefix];
  return [
    { name: `New ${noun} Text`, help: `Sent when a text reply arrives on ${owner}.`, key: `${prefix}_new_text`, group, push: true, email: true },
    { name: `New ${noun} Email`, help: `Sent when an email reply arrives on ${owner}.`, key: `${prefix}_new_email`, group, push: true, email: true },
    { name: `${noun} Incoming Call`, help: `Sent when a call arrives on ${owner}.`, key: `${prefix}_incoming_call`, group, push: true, email: true },
    { name: `New ${noun} Chat`, help: `Sent when a chat message arrives on ${chatOwner(prefix)}.`, key: `${prefix}_new_chat_message`, group, push: true },
  ];
}

function chatOwner(prefix: 'service' | 'deal' | 'loan'): string {
  return prefix === 'service' ? 'an RO conversation' : prefix === 'deal' ? 'a deal conversation' : 'a loan conversation';
}

const SERVICE_PREFS = modulePrefs('service', 'Repair Order', 'an RO conversation you are the service writer for or have participated in');
const DEAL_PREFS = modulePrefs('deal', 'Deal', 'a deal conversation you are assigned to or have participated in');
const LOAN_PREFS = modulePrefs('loan', 'Loan', 'a loan conversation you are the account manager for or have participated in');

const ADMIN_PREFS: NotificationPrefType[] = [
  { name: 'Admin Title Transaction Posted', help: 'Sent each time a title transaction is posted and approved', key: 'admin_title_transaction_approved', group: 'admin', push: true },
];

/** The notification toggles a user sees, in the legacy page's order. */
export function notificationPrefTypes(ctx: NotificationPrefContext): NotificationPrefType[] {
  if (ctx.isAdmin || ctx.settingsAdmin) return ctx.isAdmin ? ADMIN_PREFS.slice() : [];
  const out: NotificationPrefType[] = ctx.dtsDealer ? [] : DEALER_PREFS.slice();
  if (!ctx.dtsDealer && ctx.routeOneFull) out.push(ROUTE_ONE_PREF);
  if (ctx.titlingAccess) out.push(...TITLE_PREFS);
  if (ctx.commsV2 && ctx.serviceCommunication) out.push(...SERVICE_PREFS);
  if (ctx.commsV2 && ctx.dealCommunication) out.push(...DEAL_PREFS);
  if (ctx.commsV2 && ctx.loanCommunication) out.push(...LOAN_PREFS);
  return out;
}

/**
 * DTS users are emailed by default (delivery treats an absent preference as
 * on), so their toggles read as on until they write an explicit false.
 */
export function notificationPrefValue(
  pref: unknown,
  type: NotificationPrefType,
  channel: 'push' | 'email',
  dtsDealer: boolean,
): boolean {
  const value = pref && typeof pref === 'object' ? (pref as Record<string, unknown>)[channel] : undefined;
  if (value === undefined && dtsDealer && type[channel]) return true;
  return value === true || value === 'true' || value === 1 || value === '1';
}

/** The visible toggles split into the page's groups, in group order; groups with no toggles are left out. */
export function groupNotificationPrefTypes(types: readonly NotificationPrefType[]): Array<NotificationPrefGroup & { types: NotificationPrefType[] }> {
  return NOTIFICATION_PREF_GROUPS.map((group) => ({ ...group, types: types.filter((type) => type.group === group.key) })).filter((group) => group.types.length > 0);
}

// ── Business schedule ──────────────────────────────────────────────────────

/** Accounts the person has hours at, plus the one on screen (a store just added is selectable before its first save). */
export function scheduleAccounts(schedule: BusinessSchedule | null): ScheduleAccount[] {
  if (!schedule) return [];
  const ids = (schedule.scheduled_dealer_ids || []).slice();
  if (ids.indexOf(schedule.dealer_id) < 0) ids.push(schedule.dealer_id);
  return (schedule.accounts || []).filter((acct) => ids.indexOf(acct.id) >= 0 && acct.viewable !== false);
}

export function addableScheduleAccounts(schedule: BusinessSchedule | null): ScheduleAccount[] {
  if (!schedule) return [];
  const ids = schedule.available_dealer_ids || [];
  return (schedule.accounts || []).filter((acct) => ids.indexOf(acct.id) >= 0 && acct.id !== schedule.dealer_id && acct.viewable !== false);
}

export function scheduleAccountName(schedule: BusinessSchedule | null, dealerId: number): string {
  const match = ((schedule && schedule.accounts) || []).find((acct) => acct.id === dealerId);
  return match ? match.name : '';
}

/** `Date` or `YYYY-MM-DD...` -> `YYYY-MM-DD` (time off is date-only). */
export function toYmd(value: string | null | undefined): string {
  return value ? String(value).slice(0, 10) : '';
}
